/**
 * Slack's "Are you sure?" before @channel / @everyone / @here notifies a crowd: sending a draft with a broadcast in a
 * channel where it would notify more than BROADCAST_WARN_OVER people asks first, saying how many people and how many
 * time zones they're in (so nobody pings a whole team at 3 a.m. by accident). Only messages that reach the channel
 * notify (thread replies only when also sent to the channel), and only channels have broadcasts.
 */
import type { Member, Presence } from "@bundle/contracts";
import type { BroadcastRange } from "./autocomplete.ts";
import { composerSegments, type ComposerChipContext } from "./composerText.ts";

/** Slack warns when a broadcast would notify more than 6 people. */
export const BROADCAST_WARN_OVER = 6;

/** Wider ranges win when a draft has several (@everyone and @channel reach everyone in the channel, @here only the active). */
const WIDTH: Record<BroadcastRange, number> = { here: 0, channel: 1, everyone: 2 };

/** The broadcast a draft uses (the widest when it has several; none inside code); undefined = none. */
export function draftBroadcast(text: string, ctx: ComposerChipContext): BroadcastRange | undefined {
  let out: BroadcastRange | undefined;
  for (const s of composerSegments(text, ctx)) {
    if (s.type === "chip" && s.chip === "broadcast" && s.range !== undefined && (out === undefined || WIDTH[s.range] > WIDTH[out])) out = s.range;
  }
  return out;
}

export type BroadcastWarning = {
  range: BroadcastRange;
  /** People it notifies (not me). */
  people: number;
  /** Distinct time zones among them; absent when the member list (or everyone's time zone) isn't known. */
  timeZones?: number;
};

export type BroadcastContext = {
  meId: string;
  members: Record<string, Member>;
  /** The channel's member count (the header's): the fallback when its member list isn't loaded (offline). */
  memberCount: number;
  /** The channel's members, when loaded. */
  memberIds?: string[];
  /** Live presence (logic/live): @here notifies the active ones. */
  presence: Record<string, Presence>;
};

/**
 * Who `range` notifies in the channel and whether that's enough people to ask first (undefined = just send). With the
 * member list: its active (not deactivated) members but me, @here only those active now; their time zones counted
 * when anyone has one. Without it: everyone in the count but me, no time zones.
 */
export function broadcastWarning(range: BroadcastRange, ctx: BroadcastContext): BroadcastWarning | undefined {
  if (ctx.memberIds === undefined) {
    const people = Math.max(0, ctx.memberCount - 1);
    return people > BROADCAST_WARN_OVER ? { range, people } : undefined;
  }
  const recipients = ctx.memberIds.filter((id) => {
    if (id === ctx.meId) return false;
    const m = ctx.members[id];
    if (m?.deactivated === true) return false;
    return range !== "here" || ctx.presence[id] === "active";
  });
  if (recipients.length <= BROADCAST_WARN_OVER) return undefined;
  const zones = new Set<string>();
  for (const id of recipients) {
    const tz = ctx.members[id]?.timezone;
    if (tz !== undefined && tz !== "") zones.add(tz);
  }
  return zones.size === 0 ? { range, people: recipients.length } : { range, people: recipients.length, timeZones: zones.size };
}
